/**
 * Plays a product's video inside its collection-card gallery slide.
 *
 * Stock renders every card medium with `preview_image_only: true`
 * (snippets/card-gallery.liquid), so a product's video is a poster frame in the
 * card and never a player — there is no <video> in the DOM at all. That snippet
 * is stock and cannot be edited, so the sources are emitted server-side as a
 * JSON payload by the card's custom-liquid block and the element is built here.
 *
 * Loading discipline, because a card video is the least valuable byte on a
 * collection page and must never compete with the ones that matter:
 *
 * A `load`/`requestIdleCallback` wrapper was tried first and silently stopped
 * the component initialising at all. It was also redundant: with preload="none"
 * and build-on-view, no video byte is requested until a shopper scrolls to that
 * slide, which is well after LCP by definition.
 *
 *   - `preload="none"`, so a card that is never scrolled to costs zero bytes;
 *   - the element is not built until its slide is scrolled into view, so the
 *     request cannot contend with the product imagery that decides LCP;
 *   - never on a metered or slow connection, and never under reduced motion.
 *
 * The poster stays visible until the video can genuinely play, so a slow file
 * shows the photograph rather than a blank cell.
 */
import { Component } from '@theme/component';

const SLIDE = '.product-media-container--video';
const REDUCED = '(prefers-reduced-motion: reduce)';

class EchtCardVideo extends Component {
  #video = null;
  #io = null;
  #motion = null;

  /** Metered or slow connections never get a card video. */
  get #connectionAllows() {
    const c = navigator.connection;
    if (!c) return true;
    if (c.saveData) return false;
    return !['slow-2g', '2g', '3g'].includes(c.effectiveType);
  }

  connectedCallback() {
    super.connectedCallback();

    this.#motion = window.matchMedia(REDUCED);
    if (this.#motion.matches) return;

    if (!this.#connectionAllows) return;

    const card = this.closest('.product-grid__item, .resource-list__item');
    const slide = card?.querySelector(SLIDE);
    if (!slide) return;

    // Sources come from an attribute, not text content. As text they printed
    // under every card, and hiding the element with `display: none` stopped it
    // initialising — an attribute needs neither.
    let sources;
    try {
      sources = JSON.parse(this.dataset.sources || '[]');
    } catch {
      return;
    }
    if (!sources.length) return;

    // Play only while the slide is the one on screen. `root: null` is correct
    // even though the slides sit in a horizontal scroller: a slide scrolled out
    // sideways is outside the viewport too.
    this.#watch(slide, sources);
  }

  #watch(slide, sources) {
    this.#io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#ensure(slide, sources);
            this.#video?.play().catch(() => {});
          } else {
            this.#video?.pause();
          }
        }
      },
      // 0.4, not a half or more: a card slide is ~514px tall and the viewport
      // is shared with a sticky header, so a slide the shopper is plainly
      // looking at commonly tops out around 0.55. Measured, not guessed.
      { threshold: 0.4 }
    );
    this.#io.observe(slide);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#io?.disconnect();
  }

  /** Builds the element once, on first sight. */
  #ensure(slide, sources) {
    if (this.#video) return;

    const video = document.createElement('video');
    video.className = 'echt-card-video__el';
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = 'none';
    // autoplay is not set: the element is created while already in view, so
    // nothing would start it, and a scripted play() here is a user-visible
    // action rather than page load — Chrome allows it for muted video.
    // MP4 only, defensively. The sources payload is emitted by the card's
    // custom-liquid block in templates/*.json, which is edited outside this
    // file; on 6 Sep 2026 it listed the .m3u8 stream first and Android Chrome —
    // which claims application/vnd.apple.mpegurl and then commonly never starts
    // — selected it and stalled ("the video tile for the homepage Best Sellers
    // for the 2nd product listed doesn't play", client, 6 Sep 2026). Filtering
    // here means a stray HLS source can never be first again whatever the
    // template emits.
    for (const source of sources) {
      if (source.type !== 'video/mp4') continue;
      const el = document.createElement('source');
      el.src = source.src;
      el.type = source.type;
      video.append(el);
    }
    // Nothing playable left: keep the poster rather than append an empty
    // <video> over it.
    if (!video.querySelector('source')) return;

    // The poster is the slide's own image. It stays put and on top until the
    // video reports it can actually play — a card that blanks while a file
    // downloads is worse than one that never had a video.
    const poster = slide.querySelector('img');
    if (poster?.currentSrc) video.poster = poster.currentSrc;

    video.addEventListener(
      'playing',
      () => {
        video.classList.add('is-playing');
      },
      { once: true }
    );

    slide.append(video);
    this.#video = video;
  }
}

if (!customElements.get('echt-card-video')) {
  customElements.define('echt-card-video', EchtCardVideo);
}
